import React, { useState, useMemo } from "react"
import { motion } from "framer-motion"
import {CheckCircle, Trash2, Eye, Download, User, DollarSign, Printer, Filter, FileText, X, FileQuestion, AlertCircle} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import toast from "react-hot-toast"
import QRCodeLib from "qrcode"
import { gerarPixPayload } from "../utils/pixQrCode"

export default function PagamentosConfirmados() {
  const { pagamentos, deletePagamento, marcarComoPago, dadosPrefeitura } = useGuias()
  const [processandoPagamento, setProcessandoPagamento] = useState<string | null>(null)
  const [pagamentoSelecionado, setPagamentoSelecionado] = useState<any>(null)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("")
  // Data inicial sempre igual à data atual ao abrir a tela (formato YYYY-MM-DD, sem problemas de timezone)
  const obterDataAtualFormatada = () => {
    const hoje = new Date()
    const ano = hoje.getFullYear()
    const mes = String(hoje.getMonth() + 1).padStart(2, "0")
    const dia = String(hoje.getDate()).padStart(2, "0")
    return `${ano}-${mes}-${dia}`
  }

  const [dataInicio, setDataInicio] = useState<string>(() => obterDataAtualFormatada())
  const [dataFim, setDataFim] = useState<string>("")

  // Filtrar pagamentos confirmados e aplicar filtro de data
  const pagamentosConfirmados = useMemo(() => {
    let filtrados = pagamentos.filter(p => p.statusPagamento === "pago")
    
    if (dataInicio) {
      filtrados = filtrados.filter(p => {
        if (!p.dataPagamento) return false
        
        const dataPagamento = new Date(p.dataPagamento)
        const dataInicioComHora = new Date(dataInicio + "T00:00:00")
        
        return dataPagamento >= dataInicioComHora
      })
    }
    
    if (dataFim) {
      filtrados = filtrados.filter(p => {
        if (!p.dataPagamento) return false
        
        const dataPagamento = new Date(p.dataPagamento)
        const dataFimComHora = new Date(dataFim + "T23:59:59")
        
        return dataPagamento <= dataFimComHora
      })
    }
    
    // Ordenar por data de pagamento (mais recente primeiro)
    filtrados.sort((a, b) => {
      const dataA = a.dataPagamento ? new Date(a.dataPagamento).getTime() : 0
      const dataB = b.dataPagamento ? new Date(b.dataPagamento).getTime() : 0
      return dataB - dataA
    })
    
    return filtrados
  }, [pagamentos, dataInicio, dataFim])

  // Filtrar pagamentos sem comprovante
  const pagamentosSemComprovante = useMemo(() => {
    return pagamentos.filter(p => p.statusPagamento === "sem_comprovante")
  }, [pagamentos])

  const handleDeletar = async (id: string, numeroComprovante: string) => {
    if (window.confirm(`Deseja realmente excluir o pagamento confirmado ${numeroComprovante}?`)) {
      await deletePagamento(id)
    }
  }

  const handleVisualizarComprovante = async (pagamento: any) => {
    setPagamentoSelecionado(pagamento)
    
    // Gerar QR Code
    const pixPayload = gerarPixPayload(
      dadosPrefeitura?.chavePix || "",
      (dadosPrefeitura?.nomePrefeitura || "Prefeitura").substring(0, 25),
      "Sao Jose do Serido",
      pagamento.valor,
      pagamento.numeroComprovante
    )
    
    try {
      const qrCodeUrl = await QRCodeLib.toDataURL(pixPayload, {
        width: 180,
        margin: 1,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      })
      setQrCodeDataUrl(qrCodeUrl)
    } catch (error) {
      console.error("Erro ao gerar QR Code:", error)
      toast.error("Erro ao gerar QR Code")
    }
  }

  const handleImprimirComprovante = () => {
    window.print()
  }

  const handleFecharComprovante = () => {
    setPagamentoSelecionado(null)
    setQrCodeDataUrl("")
  }

  const handleConfirmarPagamento = async (pagamento: any) => {
    if (processandoPagamento === pagamento._id) {
      toast.error("Aguarde! Processando pagamento...")
      return
    }
    
    setProcessandoPagamento(pagamento._id!)
    
    try {
      await marcarComoPago(pagamento._id!)
      toast.success("Pagamento confirmado com sucesso!")
    } catch (error) {
      console.error("Erro ao confirmar pagamento:", error)
    } finally {
      setProcessandoPagamento(null)
    }
  }

  const handleImprimirListagem = () => {
    // Calcular total
    const totalPagamentos = pagamentosConfirmados.length
    const somaTotal = pagamentosConfirmados.reduce((acc, p) => {
      return acc + parseFloat(p.valor.replace(/[^\d,.-]/g, "").replace(",", "."))
    }, 0)

    // Abrir nova aba com a listagem
    const novaAba = window.open("", "_blank")
    if (!novaAba) {
      toast.error("Bloqueador de pop-up ativo. Permita pop-ups para imprimir.")
      return
    }

    const linhasTabela = pagamentosConfirmados.map((pag, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${pag.numeroComprovante}</td>
        <td>${pag.nomePagador}</td>
        <td>${pag.cpfPagador}</td>
        <td>${pag.referenciaPagamento}</td>
        <td style="font-weight: 600;">R$ ${pag.valor}</td>
        <td>${pag.dataPagamento ? new Date(pag.dataPagamento).toLocaleDateString("pt-BR") : "N/A"}</td>
      </tr>
    `).join("")

    novaAba.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Pagamentos Confirmados</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 30px 20px; background: #fff; }
          .container { max-width: 1200px; margin: 0 auto; background: white; border: 2px solid #004161; }
          
          /* Cabeçalho Institucional com Logo */
          .cabecalho { display: flex; align-items: center; padding: 20px; border-bottom: 3px solid #004161; background: linear-gradient(to right, #f0f9ff, #ffffff); }
          .logo-prefeitura { width: 100px; height: 100px; margin-right: 20px; flex-shrink: 0; object-fit: contain; }
          .dados-prefeitura { flex: 1; }
          .dados-prefeitura h1 { font-size: 22px; font-weight: bold; color: #004161; margin-bottom: 8px; text-transform: uppercase; border-bottom: 2px solid #004161; padding-bottom: 4px; }
          .dados-prefeitura p { font-size: 11px; color: #333; margin: 2px 0; line-height: 1.5; }
          
          /* Título do Relatório */
          .titulo-relatorio { background: #004161; padding: 15px; text-align: center; }
          .titulo-relatorio h2 { font-size: 18px; font-weight: bold; color: white; letter-spacing: 1px; }
          
          /* Info Box */
          .info-box { background: #e0f2fe; border-left: 4px solid #0891b2; padding: 16px 20px; margin: 20px; border-radius: 4px; }
          .info-box p { color: #075985; font-size: 13px; margin: 6px 0; }
          .info-box strong { color: #0c4a6e; font-weight: 600; }
          
          /* Tabela */
          .tabela-container { padding: 0 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          thead { background: linear-gradient(to right, #004161, #0891b2); }
          thead th { color: white; padding: 14px 10px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
          thead th:first-child { text-align: center; }
          thead th:nth-last-child(-n+2) { text-align: center; }
          tbody tr { border-bottom: 1px solid #e5e7eb; transition: background 0.2s; }
          tbody tr:hover { background: #f0f9ff; }
          tbody tr:nth-child(even) { background: #f9fafb; }
          tbody td { font-size: 11px; color: #374151; padding: 12px 10px; }
          tbody td:first-child { text-align: center; font-weight: 600; color: #004161; }
          tbody td:nth-last-child(-n+2) { text-align: center; }
          tbody td:nth-child(6) { font-weight: 600; color: #0d9488; }
          
          /* Totais */
          .totais { margin: 20px; padding: 20px; background: linear-gradient(135deg, #e0f2fe 0%, #f0fdfa 100%); border-radius: 8px; border: 2px solid #0891b2; }
          .totais-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
          .total-item { text-align: center; padding: 15px; background: white; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
          .total-item .label { font-size: 11px; color: #6b7280; text-transform: uppercase; margin-bottom: 8px; font-weight: 600; letter-spacing: 0.5px; }
          .total-item .value { font-size: 24px; font-weight: bold; color: #004161; }
          
          /* Rodapé */
          .footer { margin: 20px; padding: 20px; border-top: 2px solid #e5e7eb; text-align: center; background: #f9fafb; }
          .footer p { font-size: 10px; color: #6b7280; margin: 4px 0; line-height: 1.6; }
          .footer .destaque { color: #004161; font-weight: 600; font-size: 11px; }
          
          @media print {
            body { padding: 0; background: white; }
            .container { box-shadow: none; border: 2px solid #004161; }
            tbody tr:hover { background: transparent; }
            @page { margin: 15mm; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Cabeçalho com Logo e Dados da Prefeitura -->
          <div class="cabecalho">
            <img src="${dadosPrefeitura?.logoUrl || 'https://lumi.new/lumi.ing/logo.png'}" alt="Logo" class="logo-prefeitura" />
            <div class="dados-prefeitura">
              <h1>${dadosPrefeitura?.nomePrefeitura || 'Prefeitura Municipal'}</h1>
              <p><strong>CNPJ:</strong> ${dadosPrefeitura?.cnpj || 'Não informado'}</p>
              <p><strong>Endereço:</strong> ${dadosPrefeitura?.endereco || 'Não informado'}</p>
              <p><strong>CEP:</strong> ${dadosPrefeitura?.cep || 'Não informado'} | <strong>Tel:</strong> ${dadosPrefeitura?.telefone || 'Não informado'}</p>
              <p><strong>Site:</strong> ${dadosPrefeitura?.site || 'https://saojosedoserido.rn.gov.br/'}</p>
            </div>
          </div>
          
          <!-- Título do Relatório -->
          <div class="titulo-relatorio">
            <h2>RELATÓRIO DE PAGAMENTOS CONFIRMADOS</h2>
          </div>

          <div class="info-box">
            <p><strong>Período:</strong> ${dataInicio ? new Date(dataInicio).toLocaleDateString("pt-BR") : "Todos"} até ${dataFim ? new Date(dataFim).toLocaleDateString("pt-BR") : "Todos"}</p>
            <p><strong>Total de Pagamentos:</strong> ${totalPagamentos}</p>
            <p><strong>Data de Geração:</strong> ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
          </div>

          <div class="tabela-container">
            <table>
              <thead>
                <tr>
                  <th style="width: 5%;">#</th>
                  <th style="width: 15%;">Comprovante</th>
                  <th style="width: 25%;">Pagador</th>
                  <th style="width: 13%;">CPF</th>
                  <th style="width: 20%;">Referência</th>
                  <th style="width: 12%;">Valor</th>
                  <th style="width: 10%;">Data Pgto</th>
                </tr>
              </thead>
              <tbody>
                ${linhasTabela}
              </tbody>
            </table>
          </div>

          <div class="totais">
            <div class="totais-grid">
              <div class="total-item">
                <p class="label">Total de Pagamentos</p>
                <p class="value">${totalPagamentos}</p>
              </div>
              <div class="total-item">
                <p class="label">Valor Total Arrecadado</p>
                <p class="value">R$ ${somaTotal.toFixed(2).replace(".", ",")}</p>
              </div>
            </div>
          </div>

          <div class="footer">
            <p class="destaque">Sistema EMIT - ${dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal"}</p>
            <p>Relatório gerado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
            <p>Este documento foi gerado eletronicamente e possui validade legal</p>
            <p>${dadosPrefeitura?.departamento || "Departamento de Arrecadação"} - ${dadosPrefeitura?.secretaria || ""}</p>
          </div>
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          }
        </script>
      </body>
      </html>
    `)
    novaAba.document.close()
  }

  const handleImprimirNovaAba = async (pagamento: any) => {
    try {
      // Gerar QR Code
      const pixPayload = gerarPixPayload(
        dadosPrefeitura?.chavePix || "",
        (dadosPrefeitura?.nomePrefeitura || "Prefeitura").substring(0, 25),
        "Sao Jose do Serido",
        pagamento.valor,
        pagamento.numeroComprovante
      )
      
      const qrCodeUrl = await QRCodeLib.toDataURL(pixPayload, {
        width: 180,
        margin: 1,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      })

      // Abrir nova aba com o comprovante
      const novaAba = window.open("", "_blank")
      if (!novaAba) {
        toast.error("Bloqueador de pop-up ativo. Permita pop-ups para imprimir.")
        return
      }

      novaAba.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Talão PIX ${pagamento.numeroComprovante}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; background: #fff; }
            .talao { max-width: 800px; margin: 0 auto; background: white; border: 2px solid #333; }
            
            /* Cabeçalho Institucional */
            .cabecalho { display: flex; align-items: center; padding: 20px; border-bottom: 3px solid #333; }
            .logo-prefeitura { width: 100px; height: 100px; margin-right: 20px; flex-shrink: 0; }
            .dados-prefeitura { flex: 1; }
            .dados-prefeitura h1 { font-size: 20px; font-weight: bold; color: #004161; margin-bottom: 8px; text-transform: uppercase; border-bottom: 2px solid #004161; padding-bottom: 4px; }
            .dados-prefeitura p { font-size: 11px; color: #333; margin: 2px 0; line-height: 1.4; }
            
            /* Título do Talão */
            .titulo-talao { background: #d0d0d0; padding: 12px; text-align: center; border-bottom: 2px solid #333; }
            .titulo-talao h2 { font-size: 18px; font-weight: bold; color: #333; letter-spacing: 1px; }
            
            /* Status de Pagamento Confirmado */
            .status-pago { background: #DBEAFE; border: 3px solid #3B82F6; padding: 15px; text-align: center; border-bottom: 2px solid #333; }
            .status-pago p { color: #1E40AF; font-weight: bold; margin: 4px 0; font-size: 14px; }
            .status-pago .destaque { font-size: 16px; text-transform: uppercase; }
            
            /* Conteúdo em 2 colunas */
            .conteudo { display: grid; grid-template-columns: 1fr 1fr; }
            
            /* Coluna Esquerda - Dados do Pagador */
            .coluna-esquerda { padding: 20px; border-right: 2px solid #333; }
            .coluna-esquerda h3 { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 15px; text-transform: uppercase; }
            .campo { margin-bottom: 12px; }
            .campo-label { font-size: 11px; font-weight: bold; color: #333; margin-bottom: 2px; }
            .campo-valor { font-size: 12px; color: #000; }
            
            /* Coluna Direita - Pagamento via PIX */
            .coluna-direita { padding: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
            .coluna-direita h3 { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 15px; text-transform: uppercase; }
            .qr-code-box { border: 3px solid #3B82F6; padding: 10px; background: white; margin-bottom: 15px; opacity: 0.6; position: relative; }
            .qr-code-box img { display: block; width: 200px; height: 200px; filter: grayscale(100%); }
            .carimbo-pago { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-15deg); border: 5px solid #3B82F6; background: rgba(59, 130, 246, 0.95); padding: 15px 30px; border-radius: 12px; box-shadow: 0 8px 16px rgba(0,0,0,0.3); }
            .carimbo-pago p { color: white; font-weight: 900; font-size: 32px; letter-spacing: 8px; margin: 0; text-shadow: 3px 3px 6px rgba(0,0,0,0.6); }
            .info-pagamento { width: 100%; }
            .info-pagamento .campo { margin-bottom: 8px; text-align: center; }
            .info-pagamento .campo-label { font-size: 11px; font-weight: bold; color: #333; }
            .info-pagamento .campo-valor { font-size: 13px; color: #000; font-weight: 600; }
            
            /* Rodapé */
            .rodape { padding: 12px 20px; border-top: 2px solid #333; text-align: center; background: #f5f5f5; }
            .rodape p { font-size: 10px; color: #333; margin: 2px 0; }
            .rodape .confirmado { color: #3B82F6; font-weight: bold; font-size: 11px; }
            
            @media print {
              body { padding: 0; background: white; }
              .talao { border: 2px solid #000; }
              @page { margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="talao">
            <!-- Cabeçalho com Logo e Dados da Prefeitura -->
            <div class="cabecalho">
              <img src="${dadosPrefeitura?.logoUrl || 'https://lumi.new/lumi.ing/logo.png'}" alt="Logo" class="logo-prefeitura" />
              <div class="dados-prefeitura">
                <h1>${dadosPrefeitura?.nomePrefeitura || 'Prefeitura Municipal'}</h1>
                <p><strong>CNPJ:</strong> ${dadosPrefeitura?.cnpj || 'Não informado'}</p>
                <p><strong>Endereço:</strong> ${dadosPrefeitura?.endereco || 'Não informado'}</p>
                <p><strong>CEP:</strong> ${dadosPrefeitura?.cep || 'Não informado'} | <strong>Tel:</strong> ${dadosPrefeitura?.telefone || 'Não informado'}</p>
                <p><strong>Site:</strong> ${dadosPrefeitura?.site || 'https://saojosedoserido.rn.gov.br/'}</p>
              </div>
            </div>
            
            <!-- Título do Talão -->
            <div class="titulo-talao">
              <h2>COMPROVANTE DE PAGAMENTO PIX</h2>
            </div>
            
            <!-- Status de Pagamento Confirmado -->
            <div class="status-pago">
              <p class="destaque">✓ PAGAMENTO CONFIRMADO</p>
              <p>Pago em: ${pagamento.dataPagamento ? new Date(pagamento.dataPagamento).toLocaleString("pt-BR") : "N/A"}</p>
              <p>Este QR Code já foi utilizado e não pode mais ser escaneado</p>
            </div>
            
            <!-- Conteúdo em 2 Colunas -->
            <div class="conteudo">
              <!-- Coluna Esquerda - Dados do Pagador -->
              <div class="coluna-esquerda">
                <h3>Dados do Pagador</h3>
                <div class="campo">
                  <div class="campo-label">Nome:</div>
                  <div class="campo-valor">${pagamento.nomePagador}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">CPF:</div>
                  <div class="campo-valor">${pagamento.cpfPagador}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Referente a:</div>
                  <div class="campo-valor">${pagamento.referenciaPagamento}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Número do Comprovante:</div>
                  <div class="campo-valor">${pagamento.numeroComprovante}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Data de Emissão:</div>
                  <div class="campo-valor">${new Date(pagamento.createdAt).toLocaleDateString("pt-BR")} às ${new Date(pagamento.createdAt).toLocaleTimeString("pt-BR")}</div>
                </div>
                ${pagamento.isNFSe && pagamento.numeroNotaFiscal ? `
                <div class="campo">
                  <div class="campo-label">NFS-e - Nota Fiscal Nº:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">${pagamento.numeroNotaFiscal}</div>
                </div>
                ` : ''}
              </div>
              
              <!-- Coluna Direita - Comprovante PIX -->
              <div class="coluna-direita">
                <h3>Comprovante PIX</h3>
                <div class="qr-code-box">
                  <img src="${qrCodeUrl}" alt="QR Code PIX" />
                  <div class="carimbo-pago">
                    <p>PAGO</p>
                  </div>
                </div>
                <p style="font-size: 11px; color: #3B82F6; font-weight: bold; margin-bottom: 15px;">QR Code Utilizado</p>
                <div class="info-pagamento">
                  <div class="campo">
                    <div class="campo-label">CHAVE PIX:</div>
                    <div class="campo-valor" style="font-size: 10px; word-break: break-all;">${pagamento.chavePix}</div>
                  </div>
                  <div class="campo">
                    <div class="campo-label">VALOR PAGO:</div>
                    <div class="campo-valor" style="font-size: 18px; color: #004161;">R$ ${pagamento.valor}</div>
                  </div>
                  <div class="campo">
                    <div class="campo-label">DATA DO PAGAMENTO:</div>
                    <div class="campo-valor">${pagamento.dataPagamento ? new Date(pagamento.dataPagamento).toLocaleDateString("pt-BR") : 'Não informado'}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Rodapé -->
            <div class="rodape">
              <p class="confirmado">✓ PAGAMENTO CONFIRMADO E PROCESSADO</p>
              <p>Este comprovante confirma que o pagamento via PIX foi realizado e processado com sucesso</p>
              <p>Código de Autenticidade: ${pagamento.numeroComprovante} | Sistema EMIT - ${dadosPrefeitura?.nomePrefeitura || 'Prefeitura'}</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
        </html>
      `)
      novaAba.document.close()
    } catch (error) {
      console.error("Erro ao gerar comprovante:", error)
      toast.error("Erro ao gerar comprovante para impressão")
    }
  }

  return (
    <div className="space-y-6">
      {/* Seção de Pagamentos Sem Comprovante */}
      {pagamentosSemComprovante.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 border-2 border-orange-300"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-orange-500 rounded-lg">
              <FileQuestion className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Não Pago / Não Confirmado</h3>
              <p className="text-sm text-gray-600">Pagamentos aguardando confirmação de comprovante</p>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <p className="text-sm text-orange-800 font-semibold">
                {pagamentosSemComprovante.length} pagamento(s) sem comprovante
              </p>
            </div>
            <p className="text-xs text-orange-700 mt-1">
              Estes pagamentos foram marcados como "sem comprovante" (incluindo os expirados) e aguardam confirmação manual.
            </p>
            <p className="text-xs text-orange-600 mt-1 font-semibold">
              ⏰ Pagamentos expirados podem ser confirmados manualmente clicando em "Confirmar"
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-orange-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Comprovante</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Pagador</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">CPF</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Referência</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Valor</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Data Geração</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Validade</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody>
                {pagamentosSemComprovante.map((pagamento, index) => (
                  <motion.tr
                    key={pagamento._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border-b border-gray-100 hover:bg-orange-50 transition-colors"
                  >
                    <td className="px-4 py-4 text-sm text-gray-800 font-medium">{pagamento.numeroComprovante}</td>
                    <td className="px-4 py-4 text-sm text-gray-800">{pagamento.nomePagador}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{pagamento.cpfPagador}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{pagamento.referenciaPagamento}</td>
                    <td className="px-4 py-4 text-sm text-gray-800 font-semibold">R$ {pagamento.valor}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {new Date(pagamento.createdAt).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                        <FileQuestion className="w-3 h-3" />
                        SEM COMPROVANTE
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleConfirmarPagamento(pagamento)}
                          disabled={processandoPagamento !== null}
                          className={`flex items-center gap-1 px-3 py-1 rounded-lg transition-colors ${
                            processandoPagamento !== null
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-green-100 text-green-700 hover:bg-green-200"
                          }`}
                          title="Confirmar Pagamento"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {processandoPagamento === pagamento._id ? "Confirmando..." : "Confirmar"}
                        </button>
                        <button
                          onClick={() => handleDeletar(pagamento._id!, pagamento.numeroComprovante)}
                          className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          title="Excluir Pagamento"
                        >
                          <Trash2 className="w-4 h-4" />
                          Apagar
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Seção de Pagamentos Confirmados */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-blue-500 rounded-lg">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Pagamentos Confirmados</h3>
            <p className="text-sm text-gray-600">Visualize todos os pagamentos que foram confirmados</p>
          </div>
        </div>

        {/* Filtros por Data */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-3 md:p-4 mb-4 md:mb-6 border border-blue-200">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
            <h4 className="text-sm md:text-base font-semibold text-gray-800">Filtrar</h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                Data Início
              </label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="w-full px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                Data Fim
              </label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="w-full px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex flex-col md:items-end gap-2">
              <button
                onClick={() => {
                  setDataInicio("")
                  setDataFim("")
                }}
                className="flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X className="w-3 h-3 md:w-4 md:h-4" />
                Limpar
              </button>
              
              <button
                onClick={handleImprimirListagem}
                className="flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title="Imprimir listagem completa do período"
              >
                <FileText className="w-3 h-3 md:w-4 md:h-4" />
                Imprimir
              </button>
            </div>
          </div>
          
          <div className="mt-2 md:mt-3 text-xs md:text-sm text-gray-600">
            <span className="font-semibold text-blue-600">{pagamentosConfirmados.length}</span> encontrado(s)
            {(dataInicio || dataFim) && (
              <span className="ml-1 md:ml-2 text-xs">
                {dataInicio && ` de ${new Date(dataInicio + "T12:00:00").toLocaleDateString("pt-BR")}`}
                {dataFim && ` até ${new Date(dataFim + "T12:00:00").toLocaleDateString("pt-BR")}`}
              </span>
            )}
          </div>
        </div>

        {/* Card Totalizador de Pagamentos Confirmados */}
        {pagamentosConfirmados.length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-3 md:p-6 mb-4 md:mb-6 border-2 border-blue-300 shadow-md">
            <h4 className="text-sm md:text-lg font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              Resumo
            </h4>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-blue-200">
                <p className="text-xs md:text-sm text-gray-600 mb-1">Total</p>
                <p className="text-xl md:text-3xl font-bold text-blue-600">{pagamentosConfirmados.length}</p>
                <p className="text-[10px] md:text-xs text-gray-500 mt-1">confirmado(s)</p>
              </div>
              <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-blue-200">
                <p className="text-xs md:text-sm text-gray-600 mb-1">Arrecadado</p>
                <p className="text-lg md:text-3xl font-bold text-green-600">
                  R$ {pagamentosConfirmados.reduce((acc, p) => {
                    const valorNumerico = parseFloat(p.valor.replace(/[^\d,.-]/g, "").replace(",", "."))
                    return acc + valorNumerico
                  }, 0).toFixed(2).replace(".", ",")}
                </p>
                <p className="text-[10px] md:text-xs text-gray-500 mt-1">total</p>
              </div>
            </div>
          </div>
        )}

        {pagamentosConfirmados.length === 0 ? (
          <div className="text-center py-8 md:py-12">
            <CheckCircle className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-3 md:mb-4" />
            <p className="text-gray-500 text-base md:text-lg">Nenhum pagamento confirmado</p>
            <p className="text-gray-400 text-xs md:text-sm mt-2">Os pagamentos confirmados aparecerão aqui</p>
          </div>
        ) : (
          <>
            {/* Tabela Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Comprovante</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Pagador</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">CPF</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Referência</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Valor</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Data Pagamento</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pagamentosConfirmados.map((pagamento, index) => (
                    <motion.tr
                      key={pagamento._id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4 text-sm text-gray-800 font-medium">{pagamento.numeroComprovante}</td>
                      <td className="px-4 py-4 text-sm text-gray-800">{pagamento.nomePagador}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{pagamento.cpfPagador}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{pagamento.referenciaPagamento}</td>
                      <td className="px-4 py-4 text-sm text-gray-800 font-semibold">R$ {pagamento.valor}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {pagamento.dataPagamento 
                          ? new Date(pagamento.dataPagamento).toLocaleString("pt-BR")
                          : "N/A"}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          <CheckCircle className="w-3 h-3" />
                          PAGO
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleVisualizarComprovante(pagamento)}
                            className="flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors"
                            title="Visualizar Comprovante"
                          >
                            <Eye className="w-4 h-4" />
                            Ver
                          </button>
                          <button
                            onClick={() => handleImprimirNovaAba(pagamento)}
                            className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                            title="Imprimir em Nova Aba"
                          >
                            <Printer className="w-4 h-4" />
                            Imprimir
                          </button>
                          <button
                            onClick={() => handleDeletar(pagamento._id!, pagamento.numeroComprovante)}
                            className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                            title="Excluir Pagamento"
                          >
                            <Trash2 className="w-4 h-4" />
                            Apagar
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards Mobile */}
            <div className="md:hidden space-y-3">
              {pagamentosConfirmados.map((pagamento, index) => (
                <motion.div
                  key={pagamento._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Comprovante</p>
                      <p className="text-sm font-bold text-gray-800">{pagamento.numeroComprovante}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700">
                      <CheckCircle className="w-3 h-3" />
                      PAGO
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <p className="text-[10px] text-gray-500">Pagador</p>
                      <p className="text-xs font-medium text-gray-800">{pagamento.nomePagador}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">CPF</p>
                      <p className="text-xs text-gray-600">{pagamento.cpfPagador}</p>
                    </div>
                  </div>

                  <div className="mb-2">
                    <p className="text-[10px] text-gray-500">Referência</p>
                    <p className="text-xs text-gray-600 truncate">{pagamento.referenciaPagamento}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <p className="text-[10px] text-gray-500">Valor</p>
                      <p className="text-sm font-bold text-green-600">R$ {pagamento.valor}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Data Pgto</p>
                      <p className="text-xs text-gray-600">
                        {pagamento.dataPagamento 
                          ? new Date(pagamento.dataPagamento).toLocaleDateString("pt-BR")
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-1.5">
                    <button
                      onClick={() => handleVisualizarComprovante(pagamento)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-xs"
                    >
                      <Eye className="w-3 h-3" />
                      Ver
                    </button>
                    <button
                      onClick={() => handleImprimirNovaAba(pagamento)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-xs"
                    >
                      <Printer className="w-3 h-3" />
                      Imprimir
                    </button>
                    <button
                      onClick={() => handleDeletar(pagamento._id!, pagamento.numeroComprovante)}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-xs"
                      title="Excluir"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </motion.div>

      {/* Modal de Visualização do Comprovante */}
      {pagamentoSelecionado && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 no-print"
          onClick={handleFecharComprovante}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botões de Ação */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center z-10 no-print">
              <h3 className="text-lg font-bold text-gray-800">Comprovante de Pagamento Confirmado</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleImprimirComprovante}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Imprimir
                </button>
                <button
                  onClick={handleFecharComprovante}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>

            {/* Comprovante */}
            <div className="p-6 print-comprovante">
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 shadow-2xl">
                {/* Cabeçalho bancário */}
                <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-t-lg p-3 -mx-6 -mt-6 mb-3">
                  <div className="text-center text-white">
                    <h2 className="text-lg font-bold mb-0.5">COMPROVANTE DE PAGAMENTO PIX</h2>
                    <p className="text-xs opacity-90">Sistema de Arrecadação Municipal</p>
                  </div>
                </div>

                {/* Status de Pagamento Confirmado */}
                <div className="bg-blue-100 border-2 border-blue-500 rounded-lg p-3 mb-2">
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <p className="text-blue-800 font-bold uppercase">PAGAMENTO CONFIRMADO</p>
                  </div>
                  <p className="text-xs text-blue-700 text-center mt-1">
                    Pago em: {pagamentoSelecionado.dataPagamento ? new Date(pagamentoSelecionado.dataPagamento).toLocaleString("pt-BR") : "N/A"}
                  </p>
                  <p className="text-xs text-blue-700 text-center font-semibold">Este QR Code já foi utilizado</p>
                </div>

                {/* Número e Data */}
                <div className="bg-white rounded-lg p-2 mb-2 shadow-md border-l-4 border-blue-500">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Número do Comprovante</p>
                      <p className="text-sm font-bold text-gray-800">{pagamentoSelecionado.numeroComprovante}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Data de Geração</p>
                      <p className="text-xs font-semibold text-gray-800">
                        {new Date(pagamentoSelecionado.createdAt).toLocaleDateString("pt-BR")}
                      </p>
                      <p className="text-xs text-gray-600">
                        {new Date(pagamentoSelecionado.createdAt).toLocaleTimeString("pt-BR")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Valor Destaque */}
                <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg p-2 mb-2 text-center shadow-lg">
                  <p className="text-white text-xs uppercase tracking-wide mb-0.5">Valor da Transação</p>
                  <p className="text-white text-xl font-bold">R$ {pagamentoSelecionado.valor}</p>
                </div>

                {/* Dados da Transação */}
                <div className="bg-white rounded-lg p-2 mb-2 shadow-md">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Pagador */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-teal-600" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-700 uppercase">Pagador</h3>
                      </div>
                      <div className="pl-10 space-y-1">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Nome</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.nomePagador}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">CPF</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.cpfPagador}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Referente a</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.referenciaPagamento}</p>
                        </div>
                      </div>
                    </div>

                    {/* Recebedor */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-cyan-600" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-700 uppercase">Recebedor</h3>
                      </div>
                      <div className="pl-10 space-y-1">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Beneficiário</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.nomeRecebedor}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">CNPJ</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.cnpjRecebedor}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Chave PIX</p>
                          <p className="text-sm font-mono text-gray-800 break-all">{pagamentoSelecionado.chavePix}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* QR Code Utilizado */}
                <div className="bg-white rounded-lg p-2 shadow-md">
                  <div className="text-center">
                    <p className="text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">QR Code PIX (Utilizado)</p>
                    <div className="bg-white p-1 mx-auto rounded-lg border-2 border-blue-500 inline-block opacity-60">
                      {qrCodeDataUrl && (
                        <div className="relative">
                          <img 
                            src={qrCodeDataUrl} 
                            alt="QR Code PIX" 
                            className="w-28 h-28 mx-auto grayscale"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="transform -rotate-12">
                              <div className="border-4 border-blue-600 bg-blue-500 bg-opacity-90 px-6 py-3 rounded-lg shadow-2xl">
                                <p className="text-white font-black text-2xl tracking-widest" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.5)'}}>PAGO</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-blue-600 mt-1 font-semibold">
                      ✓ Pagamento confirmado - QR Code já foi utilizado
                    </p>
                  </div>
                </div>

                {/* Rodapé */}
                <div className="mt-2 pt-2 border-t border-gray-300">
                  <div className="text-center space-y-1">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <p className="text-xs text-blue-700 font-semibold">Pagamento Confirmado</p>
                    </div>
                    <p className="text-xs text-gray-500">Este documento comprova a confirmação do pagamento via PIX</p>
                    <p className="text-xs text-gray-500">Data de pagamento: {pagamentoSelecionado.dataPagamento ? new Date(pagamentoSelecionado.dataPagamento).toLocaleString("pt-BR") : "N/A"}</p>
                    <p className="text-xs text-gray-400 mt-1">Autenticidade: {pagamentoSelecionado.numeroComprovante}</p>
                    <p className="text-xs text-gray-400">Gerado por: Sistema EMIT - {dadosPrefeitura?.nomePrefeitura}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}
